import clsx from "clsx";
import { useState } from "react";
import { OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import {
  Check,
  Exclamation,
  ExclamationTriangleFill,
  TrashFill,
} from "react-bootstrap-icons";
import { Link } from "react-router-dom";
import { useInterval } from "usehooks-ts";
import { AlertType, DocumentStatus, DocumentType } from "@/briefpoint-client";
import ConfirmDeleteDocumentModal from "@/components/ConfirmDeleteDocumentModal/";
import Help from "@/components/Help";
import type { DocumentClient } from "@/lib/types";
import { formatLongDate } from "@/lib/utils";
import styles from "./ProcessingBox.module.scss";

interface Props {
  document: DocumentClient;
  processingType?: string;
  estimatedProgress: number;
  setEstimatedProgress: (docId: string, progress: number) => void;
  onDelete: () => void;
}

export default function ProcessingBox({
  document,
  processingType = "Processing",
  estimatedProgress,
  setEstimatedProgress,
  onDelete,
}: Props) {
  const uploadedDateTime = formatLongDate(document.uploadDate);
  const filename = document.name;
  const error = document.status === DocumentStatus.Error;
  const fixInProgress = document.alert === AlertType.FixInProgress;
  const progress = (document.info?.parsingProgress ?? 0) * 100;
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // This interval estimates the current load percentage
  const tickRt = 500;
  const identifyPercentage = 40;
  const averageTimeToIdentifyFileType = 20000; // 20 seconds in ms

  useInterval(() => {
    if (progress !== 100) {
      // If the progress is above the estimated progress, update it
      if (progress > estimatedProgress) {
        // Set the estimated progress to a percentage of the difference to smooth it out
        const p = (progress - estimatedProgress) * 0.1;
        setEstimatedProgress(document.id, estimatedProgress + p);
      }
      // Else if we have not identified the file, tick up
      else if (estimatedProgress <= identifyPercentage) {
        const amtToAdd =
          averageTimeToIdentifyFileType / tickRt / identifyPercentage;
        setEstimatedProgress(document.id, estimatedProgress + amtToAdd);
      }
    } else {
      // We have finished; quickly fill the bar
      if (progress - estimatedProgress > 5) {
        setEstimatedProgress(document.id, (estimatedProgress + progress) / 2);
      } else {
        setEstimatedProgress(document.id, 100);
      }
    }
  }, tickRt);

  const showComplete = estimatedProgress >= 100;

  const containerClassName = fixInProgress
    ? fixInProgress
    : error
      ? "error"
      : showComplete
        ? "complete"
        : processingType;
  let processingText = "";
  if (!error && estimatedProgress > 0 && estimatedProgress <= 39) {
    processingText = "Identifying document...";
  } else if (!error && estimatedProgress > 39 && estimatedProgress <= 99) {
    processingText = `Extracting ${document.info?.documentType === DocumentType.Pleading ? "case data" : "requests"}...`;
  }

  const heading = fixInProgress
    ? "Fix in progress for"
    : error
      ? `Error ${processingType.toLowerCase()}`
      : showComplete
        ? `${processingType} complete:`
        : uploadedDateTime
          ? `Currently ${processingType.toLowerCase()}`
          : "Uploading";

  return (
    <>
      <ConfirmDeleteDocumentModal
        showDeleteConfirm={showDeleteConfirm}
        setShowDeleteConfirm={setShowDeleteConfirm}
        document={document}
        onDelete={onDelete}
      />
      <div
        id={`processing-box-${document.id}`}
        className={clsx(styles.processingBox, {
          [styles.complete]: containerClassName === "complete",
          [styles.error]: containerClassName === "error",
          [styles.fixInProgress]: containerClassName === "fixInProgress",
        })}
      >
        <div className={styles.details}>
          {fixInProgress ? (
            <div className={styles.fixInProgress}>
              <ExclamationTriangleFill />
            </div>
          ) : error ? (
            <div className={styles.errorIcon}>
              <Exclamation />
            </div>
          ) : showComplete ? (
            <div className={styles.completeIcon}>
              <Check />
            </div>
          ) : (
            <Spinner
              style={{ width: "1.3rem", height: "1.3rem" }}
              animation="border"
              variant="success"
            >
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          )}
          <div className={styles.details}>
            <div className={styles.detailsInner}>
              <small>
                {heading} <b>{filename}.</b>
              </small>
              {processingText && (
                <small className={styles.detailsInnerSmall}>
                  {processingText}
                </small>
              )}
              {fixInProgress ? (
                <small className={styles.headerTwoWrap}>
                  Please check back later.
                </small>
              ) : null}
              {error && (
                <Help
                  id={`${document.id}-error`}
                  text="Briefpoint encountered an issue processing your document and will investigate further, you can also let us know by using the Feedback button on the right."
                />
              )}
              {/* This text should be smaller than small  */}
              {uploadedDateTime && showComplete && (
                <small className={styles.detailsInnerSmall}>
                  Uploaded {uploadedDateTime}.
                </small>
              )}
            </div>
          </div>
          {showComplete && !error && (
            <div className={styles.startedLinkWrapper}>
              <Link
                className="btn btn-outline-secondary btn-sm wide btn-hover-bg-fix"
                to={`/wizard/${document.id}/source-document-review?document_type=false`}
              >
                Get Started
              </Link>
            </div>
          )}
          <div className={styles.deleteButtonContainer}>
            <OverlayTrigger overlay={<Tooltip>Delete Permanently</Tooltip>}>
              <TrashFill
                onClick={() => setShowDeleteConfirm(true)}
                className="archive-icon cursor-pointer"
                title="Delete"
              />
            </OverlayTrigger>
          </div>
        </div>
        {!(estimatedProgress >= 100) && !error && (
          <div
            className={styles.loadingBar}
            style={{ width: `${estimatedProgress}%` }}
          />
        )}
      </div>
    </>
  );
}
